// Iter 134e — Admin: Shock Games sponsor playlist management.
//
// CRUD over the rotating sponsor playlist that powers the Shock Games
// carousel pill.  Shows each sponsor's impression / click / CTR
// metrics so you can prove the placement is earning its CPM.
//
// Route: /admin/sponsors
//
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Trash2, Pencil, RefreshCcw, Megaphone, ExternalLink, Eye,
  MousePointerClick, Activity, X,
} from "lucide-react";
import TopNav from "../components/TopNav";
import { toast } from "sonner";

const EMPTY = {
  name: "", tagline: "Brought to you by", logo_url: "", click_url: "",
  active: true, weight: 1, utm_source: "ndakubet", utm_campaign: "shock_games",
};

const SponsorRow = ({ s, onEdit, onDelete, onToggle }) => (
  <tr
    className="border-b border-border/40 hover:bg-muted/30 transition-colors"
    data-testid={`sponsor-row-${s.id}`}
  >
    <td className="py-3 pr-3">
      <div className="flex items-center gap-2.5 min-w-0">
        {s.logo_url ? (
          <img
            src={s.logo_url}
            alt={s.name}
            className="w-8 h-8 rounded-md object-contain bg-muted/50 p-1 shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="w-8 h-8 rounded-md bg-muted/50 shrink-0" />
        )}
        <div className="min-w-0">
          <p className="font-bold text-sm truncate">{s.name}</p>
          <p className="text-xs text-muted-foreground truncate">
            {s.tagline || "—"}
          </p>
        </div>
      </div>
    </td>
    <td className="py-3 pr-3 hidden md:table-cell">
      {s.click_url ? (
        <a
          href={s.click_url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-xs text-foreground/80 hover:text-foreground inline-flex items-center gap-1 max-w-[200px] truncate"
        >
          {s.click_url.replace(/^https?:\/\//, "").slice(0, 30)}
          <ExternalLink className="w-3 h-3 shrink-0" />
        </a>
      ) : <span className="text-xs text-muted-foreground">—</span>}
    </td>
    <td className="py-3 pr-3 text-right font-tabular text-xs">{s.weight}</td>
    <td className="py-3 pr-3 text-right font-tabular text-xs">
      {(s.impressions || 0).toLocaleString()}
    </td>
    <td className="py-3 pr-3 text-right font-tabular text-xs">
      {(s.clicks || 0).toLocaleString()}
    </td>
    <td className="py-3 pr-3 text-right">
      <Badge
        variant="outline"
        className={`font-tabular ${
          s.ctr >= 1 ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400"
            : s.ctr >= 0.3 ? "border-amber-500/40 text-amber-700 dark:text-amber-400"
            : "border-border text-muted-foreground"
        }`}
      >
        {Math.min(100, s.ctr ?? 0).toFixed(2)}%
      </Badge>
    </td>
    <td className="py-3 pr-3 text-center">
      <Switch
        checked={!!s.active}
        onCheckedChange={(v) => onToggle(s, v)}
        data-testid={`sponsor-toggle-${s.id}`}
      />
    </td>
    <td className="py-3 pl-2 text-right whitespace-nowrap">
      <Button
        variant="ghost" size="sm"
        onClick={() => onEdit(s)}
        data-testid={`sponsor-edit-${s.id}`}
      >
        <Pencil className="w-3.5 h-3.5" />
      </Button>
      <Button
        variant="ghost" size="sm"
        onClick={() => onDelete(s)}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
        data-testid={`sponsor-delete-${s.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </Button>
    </td>
  </tr>
);

const SponsorForm = ({ initial, onCancel, onSubmit, submitting }) => {
  const [form, setForm] = useState({ ...EMPTY, ...(initial || {}) });
  useEffect(() => { setForm({ ...EMPTY, ...(initial || {}) }); }, [initial]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="sponsor-form">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-lg">
          {initial?.id ? "Edit sponsor" : "Add sponsor"}
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="sponsor-form-cancel">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="sname">Name *</Label>
          <Input
            id="sname"
            value={form.name}
            onChange={set("name")}
            placeholder="Betway"
            data-testid="sponsor-form-name"
          />
        </div>
        <div>
          <Label htmlFor="stagline">Tagline</Label>
          <Input
            id="stagline"
            value={form.tagline}
            onChange={set("tagline")}
            placeholder="Brought to you by"
            data-testid="sponsor-form-tagline"
          />
        </div>
        <div>
          <Label htmlFor="slogo">Logo URL</Label>
          <Input
            id="slogo"
            value={form.logo_url}
            onChange={set("logo_url")}
            placeholder="https://…/logo.png"
            data-testid="sponsor-form-logo"
          />
        </div>
        <div>
          <Label htmlFor="sclick">Click URL *</Label>
          <Input
            id="sclick"
            value={form.click_url}
            onChange={set("click_url")}
            placeholder="https://partner.com/landing"
            data-testid="sponsor-form-clickurl"
          />
        </div>
        <div>
          <Label htmlFor="sweight">Weight (rotation priority)</Label>
          <Input
            id="sweight"
            type="number" min={1} max={100}
            value={form.weight}
            onChange={(e) => setForm((f) => ({ ...f, weight: Math.max(1, parseInt(e.target.value || "1", 10)) }))}
            data-testid="sponsor-form-weight"
          />
        </div>
        <div>
          <Label htmlFor="sutmsrc">UTM source</Label>
          <Input
            id="sutmsrc"
            value={form.utm_source}
            onChange={set("utm_source")}
            data-testid="sponsor-form-utmsrc"
          />
        </div>
        <div>
          <Label htmlFor="sutmcamp">UTM campaign</Label>
          <Input
            id="sutmcamp"
            value={form.utm_campaign}
            onChange={set("utm_campaign")}
            data-testid="sponsor-form-utmcamp"
          />
        </div>
        <div className="flex items-end gap-3 pb-1">
          <div className="flex items-center gap-3">
            <Switch
              checked={!!form.active}
              onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))}
              data-testid="sponsor-form-active"
            />
            <span className="text-sm font-medium">
              {form.active ? "Active" : "Disabled"}
            </span>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => onSubmit(form)}
          disabled={submitting || !form.name.trim() || !form.click_url.trim()}
          data-testid="sponsor-form-save"
        >
          {submitting ? "Saving…" : initial?.id ? "Save changes" : "Add sponsor"}
        </Button>
      </div>
    </div>
  );
};

const AdminSponsorsPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editing, setEditing] = useState(null);  // null | {} | {id...}
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/shock-games/sponsors`, { timeout: 12000 });
      setSponsors(data?.sponsors || []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/");
      } else {
        toast.error("Failed to load sponsors");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/auth"); return; }
    load();
  }, [authLoading, user, load, navigate]);

  const save = async (form) => {
    setSubmitting(true);
    try {
      if (form.id) {
        await axios.put(`${API}/admin/shock-games/sponsors/${form.id}`, form);
        toast.success("Sponsor updated");
      } else {
        await axios.post(`${API}/admin/shock-games/sponsors`, form);
        toast.success("Sponsor created");
      }
      setEditing(null);
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete sponsor "${s.name}"? Impressions + clicks history will be lost.`)) return;
    try {
      await axios.delete(`${API}/admin/shock-games/sponsors/${s.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const toggle = async (s, active) => {
    try {
      await axios.put(`${API}/admin/shock-games/sponsors/${s.id}`, { ...s, active });
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const totalImps  = sponsors.reduce((sum, s) => sum + (s.impressions || 0), 0);
  const totalClks  = sponsors.reduce((sum, s) => sum + (s.clicks || 0), 0);
  // CTR is bounded to [0, 100]. clicks can exceed impressions when an
  // impression beacon failed but the click landed (or multi-tab usage)
  // — bound for sane display.
  const aggregateCtr = totalImps ? Math.min(100, totalClks / totalImps * 100) : 0;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-sponsors-page">
      <TopNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-muted-foreground">
              Admin · Iter 134e Lab
            </p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-1">
              Shock Games Sponsors
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Rotating playlist of partners shown in the Shock Games banner pill.
              Higher weight = more rotations.  Clicks auto-append UTM params for
              attribution.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={load} disabled={refreshing}
              data-testid="sponsors-refresh-btn"
            >
              <RefreshCcw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => setEditing({})}
              data-testid="sponsors-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Add sponsor
            </Button>
          </div>
        </div>

        {/* Aggregate stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="sponsors-stat-active">
            <div className="flex items-center gap-2">
              <Megaphone className="w-4 h-4 text-amber-500" />
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                Active
              </p>
            </div>
            <p className="mt-2 font-display font-bold text-2xl font-tabular">
              {sponsors.filter((s) => s.active).length}<span className="text-muted-foreground font-normal text-base"> / {sponsors.length}</span>
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="sponsors-stat-imps">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-slate-500" />
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                Impressions
              </p>
            </div>
            <p className="mt-2 font-display font-bold text-2xl font-tabular">
              {totalImps.toLocaleString()}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="sponsors-stat-clicks">
            <div className="flex items-center gap-2">
              <MousePointerClick className="w-4 h-4 text-emerald-500" />
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                Clicks
              </p>
            </div>
            <p className="mt-2 font-display font-bold text-2xl font-tabular">
              {totalClks.toLocaleString()}
            </p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="sponsor-aggregate-ctr">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-500" />
              <p className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                Aggregate CTR
              </p>
            </div>
            <p className="mt-2 font-display font-bold text-2xl font-tabular">
              {aggregateCtr.toFixed(2)}%
            </p>
          </div>
        </div>

        {editing !== null && (
          <SponsorForm
            initial={editing}
            onCancel={() => setEditing(null)}
            onSubmit={save}
            submitting={submitting}
          />
        )}

        {loading ? (
          <div className="rounded-2xl border border-border bg-card p-12 text-center text-muted-foreground">
            Loading sponsors…
          </div>
        ) : sponsors.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center" data-testid="sponsors-empty">
            <Megaphone className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-sm font-medium text-foreground">No sponsors yet.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Add a partner to start monetising the Shock Games banner.
            </p>
            <Button size="sm" className="mt-4" onClick={() => setEditing({})}>
              <Plus className="w-4 h-4 mr-1.5" /> Add first sponsor
            </Button>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/30 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">
                    <th className="py-3 pr-3 text-left pl-4">Sponsor</th>
                    <th className="py-3 pr-3 text-left hidden md:table-cell">Click URL</th>
                    <th className="py-3 pr-3 text-right">Weight</th>
                    <th className="py-3 pr-3 text-right">Imps</th>
                    <th className="py-3 pr-3 text-right">Clicks</th>
                    <th className="py-3 pr-3 text-right">CTR</th>
                    <th className="py-3 pr-3 text-center">Active</th>
                    <th className="py-3 pr-3 text-right pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sponsors.map((s) => (
                    <SponsorRow
                      key={s.id}
                      s={s}
                      onEdit={setEditing}
                      onDelete={remove}
                      onToggle={toggle}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminSponsorsPage;
